import {Component, DestroyRef, inject, Input, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError, combineLatest, from, map, mergeMap, of, Subscription} from 'rxjs';
import {UserStatisticsService} from '../../../../service/User-statistics-service';
import {AuthService} from '../../../../service/auth.service';
import {
    formatStatisticsDate, statisticsRange, StatisticsRange, UserStatisticsAction, UserStatisticsActionDetail,
    UserStatisticsFile, UserStatisticsKind, UserStatisticsPage
} from '../../../../models/UserStatistics-model';

interface StatisticsColumn { key: string; label: string; sort?: string; }

@Component({
    selector: 'app-user-statistics-files',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink],
    templateUrl: './user-statistics-files.component.html',
    styleUrls: ['./user-statistics-files.component.css']
})
export class UserStatisticsFilesComponent implements OnInit {
    @Input() kind: UserStatisticsKind = 'pure-originals';
    private readonly service = inject(UserStatisticsService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly auth = inject(AuthService);
    private readonly destroyRef = inject(DestroyRef);
    private request?: Subscription;
    private actionRequest?: Subscription;
    private availabilityRequest?: Subscription;
    private detailRequest?: Subscription;
    userId = 0;
    userName = '';
    range: StatisticsRange = statisticsRange();
    columns: StatisticsColumn[] = [];
    page: UserStatisticsPage<UserStatisticsFile> | null = null;
    pageNumber = 0;
    pageSize = 50;
    readonly pageSizes = [25, 50, 100];
    sortAttribute = '';
    sortDirection: 'asc' | 'desc' = 'desc';
    loading = false;
    error = '';
    contextError = '';
    searchDraft = '';
    activeSearch = '';
    searchError = '';
    expandedId: number | null = null;
    actions: UserStatisticsAction[] = [];
    readonly actionAvailability = new Map<number, 'loading' | 'available' | 'none' | 'error'>();
    private readonly actionDetails = new Map<number, UserStatisticsActionDetail>();
    actionsLoading = false;
    actionsError = '';
    detailActionId: number | null = null;
    actionDetail: UserStatisticsActionDetail | null = null;
    detailLoading = false;
    detailError = '';
    readonly formatDate = formatStatisticsDate;

    get title(): string { return this.kind === 'masters' ? 'Masters' : 'Pure Originals'; }
    get queryParams(): object { return {...this.range, userName: this.userName}; }
    get firstRow(): number { return this.page?.content.length ? this.page.number * this.page.size + 1 : 0; }
    get lastRow(): number { return this.page ? this.page.number * this.page.size + this.page.content.length : 0; }
    isSuperAdmin(): boolean { return this.auth.isSuperAdmin(); }

    ngOnInit(): void {
        const master = this.kind === 'masters';
        this.sortAttribute = master ? 'masterId' : 'pureOriginalId';
        this.columns = [
            {key: 'id', label: master ? 'Master ID' : 'Pure Original ID', sort: this.sortAttribute},
            {key: 'reference', label: 'WinOLS reference', sort: master ? 'winOLSReference' : 'winOlsReference'},
            ...(!master ? [{key: 'winOlsDbName', label: 'WinOLS DB name', sort: 'winOlsDbName'}] : []),
            {key: 'actionDateTime', label: 'Last action', sort: 'workTime'},
            {key: 'dateUploaded', label: 'Uploaded', sort: 'dateUploaded'},
            ...(!master ? [
                {key: 'dateUpdated', label: 'Updated', sort: 'dateUpdated'},
                {key: 'dateExported', label: 'Exported', sort: 'dateExported'}
            ] : []),
            {key: 'ecu', label: 'ECU', sort: 'ecuBuild'},
            {key: 'vehicle', label: 'Vehicle', sort: 'vehicleProducer'},
            {key: 'status', label: 'File status'}
        ];
        if (!this.isSuperAdmin()) return;
        combineLatest([this.route.parent?.paramMap || this.route.paramMap, this.route.queryParamMap])
            .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(([params, query]) => {
                this.request?.unsubscribe();
                this.loading = false;
                this.page = null;
                this.userId = Number(params.get('id'));
                this.userName = query.get('userName')?.trim() || `User #${this.userId}`;
                this.contextError = '';
                if (!Number.isInteger(this.userId) || this.userId <= 0) {
                    this.contextError = 'A valid user ID is required to view statistics.';
                    return;
                }
                try {
                    this.range = statisticsRange(query.get('startDate'), query.get('endDate'));
                } catch (error) {
                    this.contextError = (error as Error).message;
                    return;
                }
                this.pageNumber = 0;
                this.activeSearch = '';
                this.searchDraft = '';
                this.load();
            });
    }

    load(): void {
        if (this.contextError || !this.isSuperAdmin()) return;
        this.request?.unsubscribe();
        this.closeActions();
        this.loading = true;
        this.error = '';
        this.page = null;
        this.request = this.service.getFiles(this.kind, this.userId, {
            ...this.range, pageNumber: this.pageNumber, pageSize: this.pageSize,
            sortAttribute: this.sortAttribute, sortDirection: this.sortDirection, search: this.activeSearch
        }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: page => { this.page = page; this.pageNumber = page.number; this.loading = false; },
            error: error => { this.error = this.service.errorMessage(error); this.loading = false; }
        });
    }

    changePage(pageNumber: number): void {
        if (this.loading || !this.page || pageNumber < 0 || pageNumber >= this.page.totalPages) return;
        this.pageNumber = pageNumber;
        this.load();
    }

    changePageSize(size: number): void {
        if (!this.pageSizes.includes(Number(size))) return;
        this.pageSize = Number(size);
        this.pageNumber = 0;
        this.load();
    }

    sortBy(column: StatisticsColumn): void {
        if (!column.sort || this.loading || this.activeSearch) return;
        this.sortDirection = this.sortAttribute === column.sort && this.sortDirection === 'desc' ? 'asc' : 'desc';
        this.sortAttribute = column.sort;
        this.pageNumber = 0;
        this.load();
    }

    applySearch(): void {
        const value = this.searchDraft.trim();
        if (value.length > 0 && value.length < 3) {
            this.searchError = 'Enter at least 3 characters to search.';
            return;
        }
        this.searchError = '';
        this.activeSearch = value;
        this.pageNumber = 0;
        this.load();
    }

    clearSearch(): void {
        this.searchDraft = '';
        this.applySearch();
    }

    fileId(row: UserStatisticsFile): number { return this.kind === 'masters' ? row.masterId : row.pureOriginalId; }
    reference(row: UserStatisticsFile): string { return row.winOLSReference || row.winOlsReference || '—'; }

    openFile(row: UserStatisticsFile): void {
        const id = this.fileId(row);
        if (row.isDeleted || !id) return;
        if (this.kind === 'masters') this.router.navigate(['/app/master-details', id]);
        else this.router.navigate(['/app/pure-originals/pure-original-details'], {queryParams: {pureOriginalId: id}});
    }

    toggleActions(row: UserStatisticsFile, retry = false): void {
        const id = this.fileId(row);
        const wasExpanded = this.expandedId === id;
        this.closeActions();
        if ((wasExpanded && !retry) || !id) return;
        this.expandedId = id;
        this.actionsLoading = true;
        this.actionRequest = this.service.getFileActions(this.kind, this.userId, id, this.range)
            .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: actions => {
                    this.actions = actions;
                    this.actionsLoading = false;
                    this.checkActionChanges(actions);
                },
                error: error => { this.actionsError = this.service.errorMessage(error); this.actionsLoading = false; }
            });
    }

    private closeActions(): void {
        this.actionRequest?.unsubscribe();
        this.availabilityRequest?.unsubscribe();
        this.actionAvailability.clear();
        this.actionDetails.clear();
        this.closeActionDetail();
        this.expandedId = null;
        this.actions = [];
        this.actionsLoading = false;
        this.actionsError = '';
    }

    canExpandAction(action: UserStatisticsAction): boolean {
        const statuses = this.kind === 'masters'
            ? ['UPDATED_MASTER_DETAILS', 'UPDATED_MASTER_INI_FILE', 'SYNCED_MASTER_FROM_PURE_ORIGINAL',
                'UPDATED_VERSION_COMMENT', 'UPDATED_PURE_ORIGINAL_MATCHING']
            : ['UPDATED_PURE_ORIGINAL_DETAILS', 'UPDATED_PURE_ORIGINAL_INI_FILE',
                'SYNCED_PURE_ORIGINAL_FROM_MASTER', 'UPDATED_PURE_ORIGINAL_MATCHING'];
        return !!action.recordId && statuses.includes(action.type);
    }

    canViewChanges(action: UserStatisticsAction): boolean {
        const availability = this.actionAvailability.get(action.recordId);
        return availability === 'available' || availability === 'error';
    }

    private checkActionChanges(actions: UserStatisticsAction[]): void {
        const eligible = actions.filter(action => this.canExpandAction(action));
        for (const action of eligible) this.actionAvailability.set(action.recordId, 'loading');
        this.availabilityRequest = from(eligible).pipe(
            mergeMap(action => this.service.getActionDetail(this.kind, this.userId, this.expandedId!, action, this.range).pipe(
                map(detail => ({action, detail, failed: false})),
                catchError(() => of({action, detail: null, failed: true}))
            ), 4),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(({action, detail, failed}) => {
            if (failed) {
                this.actionAvailability.set(action.recordId, 'error');
            } else if (detail?.changes?.length) {
                this.actionDetails.set(action.recordId, detail);
                this.actionAvailability.set(action.recordId, 'available');
            } else {
                this.actionAvailability.set(action.recordId, 'none');
            }
        });
    }

    toggleActionDetail(action: UserStatisticsAction, retry = false): void {
        if (!this.canExpandAction(action) || !this.canViewChanges(action) || !this.expandedId) return;
        const wasExpanded = this.detailActionId === action.recordId;
        this.closeActionDetail();
        if (wasExpanded && !retry) return;
        this.detailActionId = action.recordId;
        const cached = this.actionDetails.get(action.recordId);
        if (cached) {
            this.actionDetail = cached;
            return;
        }
        this.detailLoading = true;
        this.detailRequest = this.service.getActionDetail(this.kind, this.userId, this.expandedId, action, this.range)
            .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: detail => {
                    if (!detail?.changes?.length) {
                        this.actionAvailability.set(action.recordId, 'none');
                        this.closeActionDetail();
                        return;
                    }
                    this.actionDetails.set(action.recordId, detail);
                    this.actionAvailability.set(action.recordId, 'available');
                    this.actionDetail = detail;
                    this.detailLoading = false;
                },
                error: error => { this.detailError = this.service.errorMessage(error); this.detailLoading = false; }
            });
    }

    private closeActionDetail(): void {
        this.detailRequest?.unsubscribe();
        this.detailActionId = null;
        this.actionDetail = null;
        this.detailLoading = false;
        this.detailError = '';
    }

    fieldLabel(value: string): string {
        return this.actionLabel(value.replace(/([a-z])([A-Z])/g, '$1 $2'));
    }

    actionLabel(value: string): string {
        const text = (value || 'Action').replace(/_/g, ' ').toLowerCase();
        return text.charAt(0).toUpperCase() + text.slice(1);
    }
}
